import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen relative overflow-x-hidden text-white bg-[#0c0915]">
            {/* Background Image */}
            <div
                className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat pointer-events-none"
                style={{ backgroundImage: 'url("/mainBgImg.png")' }}
            />

            {/* Header */}
            <header className="relative z-10 pt-8 sm:pt-12 pb-6 px-4 sm:px-6 lg:px-20 max-w-[1000px] mx-auto">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-[#DCCBFD] hover:text-white transition-colors mb-8"
                    style={{ fontFamily: "'Poppins', sans-serif", fontSize: "14px" }}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Home
                </Link>

                <h1
                    className="text-white"
                    style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 700,
                        fontSize: "clamp(28px, 6vw, 48px)",
                        lineHeight: "clamp(36px, 7vw, 58px)",
                    }}
                >
                    Terms of Service
                </h1>
                <p
                    className="text-[#DCCBFD] mt-3"
                    style={{
                        fontFamily: "'Roboto', sans-serif",
                        fontWeight: 400,
                        fontSize: "clamp(13px, 2.5vw, 15px)",
                    }}
                >
                    Last updated: February 10, 2026
                </p>
            </header>

            {/* Content */}
            <main className="relative z-10 px-4 sm:px-6 lg:px-20 pb-16 sm:pb-24 max-w-[1000px] mx-auto">
                <div className="space-y-10">

                    {/* Agreement */}
                    <Section title="Agreement to Terms">
                        <p>
                            These Terms of Service ("Terms") govern your access to and use of the Eddy AI application and website (the "Service") operated by Wellcrafted Technologies ("we," "our," or "us"). By creating an account or using the Service, you agree to be bound by these Terms.
                        </p>
                        <p>
                            If you do not agree to these Terms, you must not access or use the Service. If you are using the Service on behalf of an organization, you represent that you have authority to bind that organization to these Terms.
                        </p>
                    </Section>

                    {/* Description of Service */}
                    <Section title="Description of Service">
                        <p>Eddy AI is an AI-powered study companion that provides:</p>
                        <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
                            <li><strong className="text-white">Question Scanning:</strong> Scan textbook questions, handwritten problems, equations, graphs, and diagrams using your device camera to receive step-by-step solutions.</li>
                            <li><strong className="text-white">Smart Notes:</strong> Convert book pages, PDFs, and handwritten notes into clean, organized digital notes.</li>
                            <li><strong className="text-white">Auto-Generated Quizzes:</strong> Create personalized quizzes from your study material to test knowledge and improve retention.</li>
                            <li><strong className="text-white">Study Organization:</strong> Save and organize study materials into subject folders for easy revision.</li>
                            <li><strong className="text-white">Collaboration:</strong> Share notes and study resources with classmates.</li>
                        </ul>
                        <p className="mt-3">
                            The Service is designed for students, educators, researchers, and professionals seeking to enhance their learning experience.
                        </p>
                    </Section>

                    {/* Eligibility */}
                    <Section title="Eligibility">
                        <p>
                            You must be at least 13 years of age (or the minimum age of digital consent in your jurisdiction) to use the Service. Users under 18 must have consent from a parent or legal guardian. By using the Service, you represent and warrant that you meet these eligibility requirements.
                        </p>
                    </Section>

                    {/* Account Responsibilities */}
                    <Section title="Account Responsibilities">
                        <ul className="list-disc list-inside space-y-2 ml-2">
                            <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                            <li>You are responsible for all activities that occur under your account.</li>
                            <li>You must provide accurate and complete information when creating your account.</li>
                            <li>You must notify us immediately of any unauthorized use of your account.</li>
                            <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
                        </ul>
                    </Section>

                    {/* Free and Premium Plans */}
                    <Section title="Free and Premium Plans">
                        <h4 className="text-white font-semibold mb-2" style={{ fontFamily: "'Poppins', sans-serif", fontSize: "clamp(14px, 3vw, 16px)" }}>
                            Free Plan
                        </h4>
                        <p>
                            The free plan includes basic features such as question scanning and note generation with usage limits. Access to free features may change as we update the Service.
                        </p>

                        <h4 className="text-white font-semibold mb-2 mt-6" style={{ fontFamily: "'Poppins', sans-serif", fontSize: "clamp(14px, 3vw, 16px)" }}>
                            Premium Plans
                        </h4>
                        <p>
                            Premium subscriptions provide unlimited scans, personalized quizzes, priority support, and additional features. Subscriptions are billed through the Apple App Store or Google Play Store and are subject to their respective terms and refund policies.
                        </p>
                        <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
                            <li>Subscriptions auto-renew unless cancelled at least 24 hours before the end of the current billing period.</li>
                            <li>You can manage or cancel your subscription through your device's app store settings.</li>
                            <li>We reserve the right to modify pricing with reasonable advance notice.</li>
                        </ul>
                    </Section>

                    {/* Acceptable Use */}
                    <Section title="Acceptable Use">
                        <p>You agree to use Eddy AI only for lawful, educational purposes. You must NOT:</p>
                        <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
                            <li>Use the Service to engage in academic dishonesty, including submitting AI-generated solutions as your own original work in violation of your institution's academic integrity policies.</li>
                            <li>Upload content that infringes on the intellectual property rights of others.</li>
                            <li>Upload illegal, harmful, threatening, abusive, or objectionable content.</li>
                            <li>Attempt to reverse-engineer, decompile, or extract the source code of the Service or its AI models.</li>
                            <li>Use automated tools, bots, or scrapers to access the Service.</li>
                            <li>Interfere with or disrupt the Service's infrastructure or other users' access.</li>
                            <li>Share your account credentials with others or create multiple accounts to circumvent usage limits.</li>
                            <li>Use the Service for commercial purposes without our written consent.</li>
                        </ul>
                    </Section>

                    {/* Intellectual Property */}
                    <Section title="Intellectual Property">
                        <h4 className="text-white font-semibold mb-2" style={{ fontFamily: "'Poppins', sans-serif", fontSize: "clamp(14px, 3vw, 16px)" }}>
                            Our Content
                        </h4>
                        <p>
                            The Service, including its design, features, AI models, branding, logos, and underlying technology, is owned by Wellcrafted Technologies and protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works based on our Service without permission.
                        </p>

                        <h4 className="text-white font-semibold mb-2 mt-6" style={{ fontFamily: "'Poppins', sans-serif", fontSize: "clamp(14px, 3vw, 16px)" }}>
                            Your Content
                        </h4>
                        <p>
                            You retain ownership of the content you upload to Eddy AI (e.g., photos of your notes, typed questions). By uploading content, you grant us a limited, non-exclusive license to process, analyze, and store it solely for the purpose of providing and improving the Service.
                        </p>

                        <h4 className="text-white font-semibold mb-2 mt-6" style={{ fontFamily: "'Poppins', sans-serif", fontSize: "clamp(14px, 3vw, 16px)" }}>
                            AI-Generated Content
                        </h4>
                        <p>
                            Solutions, notes, summaries, and quizzes generated by Eddy AI are provided for your personal educational use. While you may use this generated content for your studies, you should verify AI-generated solutions independently, as they may occasionally contain errors.
                        </p>
                    </Section>

                    {/* Disclaimer */}
                    <Section title="Disclaimer of Warranties">
                        <p>
                            THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT:
                        </p>
                        <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
                            <li>AI-generated solutions, notes, or quizzes will be 100% accurate or error-free.</li>
                            <li>The Service will be uninterrupted, timely, or secure.</li>
                            <li>The Service will meet all of your specific requirements.</li>
                            <li>Any errors in the Service will be corrected.</li>
                        </ul>
                        <p className="mt-3">
                            Eddy AI is a study aid and should be used as a supplementary learning tool alongside your regular coursework and study materials.
                        </p>
                    </Section>

                    {/* Limitation of Liability */}
                    <Section title="Limitation of Liability">
                        <p>
                            TO THE MAXIMUM EXTENT PERMITTED BY LAW, WELLCRAFTED TECHNOLOGIES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF YOUR USE OF THE SERVICE. THIS INCLUDES, BUT IS NOT LIMITED TO, DAMAGES FROM:
                        </p>
                        <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
                            <li>Reliance on AI-generated solutions that may be inaccurate.</li>
                            <li>Academic consequences resulting from misuse of the Service.</li>
                            <li>Loss of data or unauthorized access to your account.</li>
                            <li>Service interruptions or downtime.</li>
                        </ul>
                        <p className="mt-3">
                            Our total liability for any claims under these Terms shall not exceed the amount you paid us in the twelve (12) months preceding the claim.
                        </p>
                    </Section>

                    {/* Termination */}
                    <Section title="Termination">
                        <p>
                            We may suspend or terminate your access to the Service at any time, with or without cause, and with or without notice. You may terminate your account at any time by deleting it through the app settings.
                        </p>
                        <p>
                            Upon termination, your right to use the Service ceases immediately. We may delete your data in accordance with our Privacy Policy. Provisions that by their nature should survive termination will remain in effect, including intellectual property, disclaimers, and limitations of liability.
                        </p>
                    </Section>

                    {/* Changes to Terms */}
                    <Section title="Changes to These Terms">
                        <p>
                            We reserve the right to modify these Terms at any time. We will provide notice of material changes through the app or by email. Your continued use of the Service after changes take effect constitutes acceptance of the revised Terms. If you do not agree to the updated Terms, you must stop using the Service.
                        </p>
                    </Section>

                    {/* Governing Law */}
                    <Section title="Governing Law">
                        <p>
                            These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law principles. Any disputes arising from these Terms shall be resolved in the courts located in India.
                        </p>
                    </Section>

                    {/* Contact */}
                    <Section title="Contact Us">
                        <p>If you have questions about these Terms, please contact us:</p>
                        <div className="mt-3 space-y-1">
                            <p><strong className="text-white">Wellcrafted Technologies</strong></p>
                            <p>Email: contact@wellcraftedtech.com</p>
                            <p>Website: <a href="https://www.wellcraftedtech.com" className="text-[#8E57FA] hover:underline" target="_blank" rel="noopener noreferrer">www.wellcraftedtech.com</a></p>
                        </div>
                    </Section>

                </div>
            </main>
        </div>
    );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div>
        <h3
            className="text-white mb-4"
            style={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                fontSize: "clamp(18px, 4vw, 24px)",
                lineHeight: "clamp(26px, 5vw, 32px)",
            }}
        >
            {title}
        </h3>
        <div
            className="text-[#DCCBFD] space-y-3"
            style={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 400,
                fontSize: "clamp(14px, 2.8vw, 16px)",
                lineHeight: "clamp(22px, 4vw, 28px)",
            }}
        >
            {children}
        </div>
    </div>
);

export default TermsOfService;
